import { mainApi } from '../config/apiClient';

// ============================================
// TYPES
// ============================================
// NOT: gts_dotnetapi default System.Text.Json camelCase property naming kullanıyor.
// Backend'de C# property `Title` ise JSON'da `title`. TypeScript tiplerimizi de
// buna göre camelCase tutuyoruz. (Diğer mainApi kullanan modüllerle tutarlı.)

export type AnnouncementSeverity = 'Info' | 'Warning' | 'Critical';
export type AnnouncementScope = 'Company' | 'Branches' | 'Screen';
export type RecurrencePattern = 'Once' | 'Daily' | 'Weekly' | 'Monthly';

/** 1=Mon..7=Sun (ISO 8601). Backend WeekdaysCsv'de bu format kullanılır. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface QuickSaleAnnouncementTargetDto {
  id: number;
  targetType: 'Company' | 'Branch' | 'Screen';
  targetId: string;
}

export interface QuickSaleAnnouncementDto {
  id: number;
  companyId: string;
  title: string;
  message: string;
  severity: AnnouncementSeverity;
  startAt: string; // ISO datetime
  endAt: string;   // ISO datetime
  /** TimeSpan serialization → "HH:mm:ss" */
  dailyStartTime?: string | null;
  dailyEndTime?: string | null;
  weekdaysCsv?: string | null;
  recurrencePattern: RecurrencePattern;
  monthDay?: number | null;
  scope: AnnouncementScope;
  createdBy?: string | null;
  createdByName?: string | null;
  /** Sistem tarafından üretilen duyuru kaynağı (ör. "renewal"). Doluysa tenant panelinden düzenlenemez/silinemez. */
  systemSource?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  isDeleted: boolean;
  isCurrentlyActive: boolean;
  targets: QuickSaleAnnouncementTargetDto[];
}

export interface CreateQuickSaleAnnouncementDto {
  title: string;
  message: string;
  severity: AnnouncementSeverity;
  startAt: string;
  endAt: string;
  dailyStartTime?: string | null;
  dailyEndTime?: string | null;
  weekdaysCsv?: string | null;
  recurrencePattern: RecurrencePattern;
  monthDay?: number | null;
  scope: AnnouncementScope;
  branchIds: string[];
  screenIds: number[];
}

export interface UpdateQuickSaleAnnouncementDto extends CreateQuickSaleAnnouncementDto {
  id: number;
}

// ============================================
// API METHODS
// ============================================

const BASE = '/api/QuickSaleAnnouncement';

export const announcementApi = {
  /** admin/owner: Şirketin tüm duyurularını döner (active+inactive). */
  async getAll(): Promise<QuickSaleAnnouncementDto[]> {
    const { data } = await mainApi.get(`${BASE}/all`);
    return data || [];
  },

  /** Herkes: Bir ekran için ŞU AN aktif duyurular (marquee için). */
  async getActiveForScreen(
    screenId: number,
    branchId?: string | null
  ): Promise<QuickSaleAnnouncementDto[]> {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    const { data } = await mainApi.get(`${BASE}/for-screen/${screenId}`, { params });
    return data || [];
  },

  /** admin/owner: Per-screen yönetim modali için ekrana yayınlananların hepsi. */
  async getAllForScreen(
    screenId: number,
    branchId?: string | null
  ): Promise<QuickSaleAnnouncementDto[]> {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    const { data } = await mainApi.get(`${BASE}/for-screen/${screenId}/all`, { params });
    return data || [];
  },

  async create(dto: CreateQuickSaleAnnouncementDto): Promise<QuickSaleAnnouncementDto> {
    const { data } = await mainApi.post(`${BASE}/create`, dto);
    return data;
  },

  async update(dto: UpdateQuickSaleAnnouncementDto): Promise<QuickSaleAnnouncementDto> {
    const { data } = await mainApi.post(`${BASE}/update`, dto);
    return data;
  },

  async softDelete(id: number): Promise<void> {
    await mainApi.post(`${BASE}/delete`, { id });
  },
};

export default announcementApi;
